import { defineComponent as _defineComponent } from 'vue'
import BCXButton from '@/components/molecules/BCXButton.vue';
import BCXItemsWithPoints from '@/components/molecules/BCXItemsWithPoints.vue';
import { useMyProjects } from '@/views/my-projects/composables/useMyProjects';
import { PropType, computed } from 'vue';
import useI18n from '@/mixins/useI18n';

type ActionEvent = 'profile' | 'message' | 'accept' | 'reject' | 'withdraw' | 'extend-validity' | 'schedule-meeting';

interface ActionButton {
  key: string;
  icon: string;
  iconColor?: string;
  text: string;
  event: ActionEvent;
  // Profile needs the clicked element (popover anchor); the others don't.
  passTarget?: boolean;
  outlined?: boolean;
  color?: string | null;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'MyProjectsOfferDetailActions',
  props: {
  canPickOrReject: Boolean,
  type: {
    type: String as PropType<'made' | 'received'>,
    required: true
  },
  isOfferOpen: Boolean,
  isLoading: Boolean,
  offerStatus: {
    type: String,
    default: ''
  }
},
  emits: ['profile', 'message', 'accept', 'reject', 'withdraw', 'extend-validity', 'schedule-meeting'],
  setup(__props, { emit }) {

const props = __props;



const { isNarrow } = useMyProjects();
const { t } = useI18n();



const showWithdrawButton = computed(() => props.isOfferOpen || props.offerStatus === 'QUEUED' || props.offerStatus === 'NEGOTIATIONS');
const showExtendValidityButton = computed(() => props.isOfferOpen || props.offerStatus === 'QUEUED');
const showScheduleMeetingButton = computed(() => props.type === 'received' && (props.isOfferOpen || props.offerStatus === 'NEGOTIATIONS'));

const chatButtonText = computed(() => {
  if (props.type === 'received') {
    return t(
      props.isOfferOpen
        ? 'my-projects.offers-detail.action-buttons.start-talks'
        : 'my-projects.offers-detail.action-buttons.message'
    );
  }
  return t('my-projects.offers-detail.action-buttons.message');
});

// All visible buttons as data, so the layout can split them into rows dynamically.
const buttons = computed<ActionButton[]>(() => {
  const received = props.type === 'received';
  const list: ActionButton[] = [];

  if (received) {
    list.push({
      key: 'profile',
      icon: 'user',
      text: t('my-projects.offers-detail.action-buttons.profile'),
      event: 'profile',
      passTarget: true
    });
  }

  list.push({
    key: 'message',
    icon: 'chat',
    iconColor: 'secondary',
    text: chatButtonText.value,
    event: 'message',
    outlined: received && props.isOfferOpen,
    color: received && props.isOfferOpen ? 'background' : null
  });

  if (showScheduleMeetingButton.value) {
    list.push({
      key: 'schedule-meeting',
      icon: 'calendar-add',
      text: t('my-projects.offers-detail.action-buttons.schedule-meeting'),
      event: 'schedule-meeting',
      outlined: received && !props.isOfferOpen,
      color: received && !props.isOfferOpen ? 'background' : null
    });
  }

  if (props.canPickOrReject) {
    if (received) {
      list.push({
        key: 'reject',
        icon: 'x',
        iconColor: 'warning',
        text: t('my-projects.offers-detail.action-buttons.decline'),
        event: 'reject',
        outlined: received && !props.isOfferOpen,
        color: received && !props.isOfferOpen ? 'background' : null
      });
      list.push({
        key: 'accept',
        icon: 'check',
        iconColor: 'ok',
        text: t('my-projects.offers-detail.action-buttons.create-contract'),
        event: 'accept',
        outlined: received && !props.isOfferOpen,
        color: received && !props.isOfferOpen ? 'background' : null
      });
    } else {
      if (showWithdrawButton.value) {
        list.push({
          key: 'withdraw',
          icon: 'x',
          text: t('my-projects.offers-detail.action-buttons.withdraw'),
          event: 'withdraw'
        });
      }
      if (showExtendValidityButton.value) {
        list.push({
          key: 'extend-validity',
          icon: 'time-add',
          text: t('my-projects.offers-detail.action-buttons.extend-validity'),
          event: 'extend-validity'
        });
      }
    }
  }

  return list;
});

// 5+ buttons → first two (profile + chat) on the first row, the rest on a second row.
// Each row is its own BCXItemsWithPoints, so the point separator only appears within a
// row, never between the two rows.
const isTwoRows = computed(() => buttons.value.length >= 5);
const buttonRows = computed<ActionButton[][]>(() => (
  isTwoRows.value
    ? [buttons.value.slice(0, 2), buttons.value.slice(2)]
    : [buttons.value]
));

function onButtonClick(button: ActionButton, event: MouseEvent) {
  // Only the profile button forwards the clicked element (used as a popover anchor),
  // exactly like the original markup; the others emit without a payload.
  if (button.passTarget) emit(button.event, event.currentTarget);
  else emit(button.event);
}

return { __sfc: true,props, isNarrow, t, emit, showWithdrawButton, showExtendValidityButton, showScheduleMeetingButton, chatButtonText, buttons, isTwoRows, buttonRows, onButtonClick, BCXButton, BCXItemsWithPoints }
}

})